import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { Coins, Users, Heart, Flame, Globe, Shield, Zap } from "lucide-react";

/**
 * Spotlight banner for the auto-generated "Country of the Week" and
 * "Club of the Week" challenges. Fetches /api/plyx/challenges/weekly/featured
 * and renders two big tappable cards with a countdown to ends_at.
 *
 * Designed for the top of /plyx/challenges. Self-contained, gracefully
 * hides if the API returns no live featured pair (e.g. brand-new install
 * before the scheduler runs).
 */

const fmtCountdown = (endsAtIso) => {
  if (!endsAtIso) return null;
  const ms = new Date(endsAtIso).getTime() - Date.now();
  if (ms <= 0) return "Ended";
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  if (days >= 1) return `${days}d ${hours}h`;
  if (hours >= 1) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const SpotlightCard = ({ kind, challenge, pick, gradient, Icon, accentLabel }) => {
  const [countdown, setCountdown] = useState(() => fmtCountdown(challenge?.ends_at));
  useEffect(() => {
    if (!challenge?.ends_at) return;
    const t = setInterval(() => setCountdown(fmtCountdown(challenge.ends_at)), 30_000);
    return () => clearInterval(t);
  }, [challenge?.ends_at]);

  // Graceful: if no challenge exists yet for this week, still show the
  // upcoming pick as a teaser.
  const isLive = !!challenge;
  const title = challenge?.title || `${accentLabel}: ${pick?.name || "Coming soon"}`;
  const prizeCoins = challenge?.prize_pool_coins || 0;
  const entries = challenge?.entry_count || 0;
  const hearts = challenge?.total_hearts || 0;

  const linkTo = isLive ? `/plyx/challenges/${challenge.challenge_id}` : "/plyx/challenges";

  return (
    <Link
      to={linkTo}
      data-testid={`plyx-spotlight-${kind}`}
      className="group relative block rounded-3xl overflow-hidden border border-white/10 transition-transform hover:-translate-y-1 hover:border-white/25"
      style={{ background: gradient }}
    >
      {/* Grain overlay */}
      <div
        className="absolute inset-0 opacity-15 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><filter id='n'><feTurbulence baseFrequency='0.85'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/></svg>\")",
        }}
      />
      <div className="relative p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/30 backdrop-blur text-[10px] uppercase tracking-[0.25em] font-bold text-white/90">
            <Icon className="w-3 h-3" />
            {accentLabel}
          </div>
          {isLive && countdown && (
            <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur text-[11px] font-semibold text-white">
              <Zap className="w-3 h-3 text-[#FFB42D]" />
              <span className="tabular-nums">{countdown}</span>
              <span className="text-white/60">left</span>
            </div>
          )}
        </div>

        <h3 className="mt-4 font-display text-2xl sm:text-3xl leading-tight text-white drop-shadow">
          {title}
        </h3>

        {isLive ? (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-[13px] text-white/95 font-semibold">
              <span className="inline-flex items-center gap-1.5">
                <Coins className="w-4 h-4" /> {prizeCoins.toLocaleString()} coins
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Users className="w-4 h-4" /> {entries} {entries === 1 ? "entry" : "entries"}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Heart className="w-4 h-4" /> {hearts}
              </span>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#0A0A0A] text-xs font-bold shadow-lg group-hover:scale-105 transition-transform">
              Enter now <Flame className="w-3.5 h-3.5" />
            </div>
          </>
        ) : (
          <div className="mt-4 text-sm text-white/80">
            Launching soon — check back Monday.
          </div>
        )}
      </div>
    </Link>
  );
};

const WeeklySpotlight = () => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    plyxApi
      .get("/plyx/challenges/weekly/featured")
      .then((r) => setData(r.data || null))
      .catch(() => setData(null))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  // Hide entirely if BOTH slots are empty — avoid a sad-looking placeholder.
  if (!data?.country_challenge && !data?.club_challenge) return null;

  return (
    <div className="mb-8" data-testid="plyx-weekly-spotlight">
      <div className="flex items-baseline justify-between mb-3">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-[#FFB42D]">
          <Flame className="w-3 h-3" />
          This week&apos;s spotlight
        </div>
        <div className="text-[11px] text-white/40 tabular-nums">Week {data?.week_key}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SpotlightCard
          kind="country"
          challenge={data?.country_challenge}
          pick={data?.country_pick}
          accentLabel="Country of the Week"
          Icon={Globe}
          gradient="linear-gradient(135deg, #00E0C7 0%, #1A8FFF 60%, #6E3CFF 100%)"
        />
        <SpotlightCard
          kind="club"
          challenge={data?.club_challenge}
          pick={data?.club_pick}
          accentLabel="Club of the Week"
          Icon={Shield}
          gradient="linear-gradient(135deg, #FF2D7A 0%, #FF6B00 50%, #FFB42D 100%)"
        />
      </div>
    </div>
  );
};

export default WeeklySpotlight;
